import getConfig from 'next/config';
import { useInfiniteQuery, useQuery } from 'react-query';
import { productService } from 'services';
import { ProductsRequestParams } from 'types';
import { isProductCacheEnabled } from 'utils';

export const useFetchProducts = (productsFilter: ProductsRequestParams) => {
  const cacheKey = buildCacheKey(
    productsFilter.taxonSlug,
    productsFilter.variantIds,
    productsFilter.sortDirection,
    productsFilter.categoryFilters,
    productsFilter.skus
  );

  const { publicRuntimeConfig } = getConfig();
  const storeRegion = publicRuntimeConfig.STORE_REGION;

  return useInfiniteQuery(
    cacheKey,
    ({ pageParam = 1 }) => {
      return productService.getProducts(
        {
          taxonSlug: productsFilter.taxonSlug,
          sortDirection: productsFilter.sortDirection,
          variantIds: productsFilter.variantIds,
          categoryFilters: productsFilter.categoryFilters,
          skus: productsFilter.skus,
          pageSize: productsFilter.pageSize,
        },
        pageParam
      );
    },
    {
      getNextPageParam: (lastpage, pages) => {
        if (typeof lastpage.currentPageNumber === 'undefined') {
          return undefined;
        }

        if (lastpage.currentPageNumber === lastpage.totalPages) {
          return undefined;
        } else {
          return lastpage.currentPageNumber + 1;
        }
      },
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      keepPreviousData:
        productsFilter.categoryFilters &&
        productsFilter.categoryFilters.length > 0,

      enabled:
        !isProductCacheEnabled() &&
        (Boolean(productsFilter.taxonSlug) ||
          (typeof productsFilter.variantIds !== 'undefined' &&
            productsFilter.variantIds.length > 0) ||
          (typeof productsFilter.skus !== 'undefined' &&
            productsFilter.skus.length > 0)),
    }
  );
};

export const buildCacheKey = (
  taxonSlug?: string,
  variantIds?: string[],
  sortDirection?: string,
  categoryFilters?: string[],
  skus?: string[]
) => {
  const cacheKey = [];
  if (taxonSlug) {
    cacheKey.push('taxon');
    cacheKey.push(taxonSlug);
  }

  if (variantIds) {
    cacheKey.push('variants');
    cacheKey.push(...variantIds);
  }

  if (skus) {
    cacheKey.push('skus');
    cacheKey.push(...skus);
  }

  if (sortDirection) {
    cacheKey.push(sortDirection);
  }

  if (categoryFilters && categoryFilters.length > 0) {
    cacheKey.push('withCategoryFilter');
    cacheKey.push(categoryFilters);
  }

  return cacheKey;
};
